/**
 * LO QUE EL PANEL DEL EQUIPO NO ENSEÑA DE MOMENTO (13-10).
 *
 * Lo pidió el equipo tras las pruebas del 13-10 (hoja de los probadores, C5, C6 y C7): las
 * rutinas se llevan de momento fuera del panel, y el simulador de semanas no es una pantalla
 * de trabajo del día a día. Nada se ha borrado: las pantallas, sus rutas y su código siguen
 * donde estaban, y se entra por dirección directa (/admin/routines para el equipo,
 * /admin/simulador solo para administradores). Aquí solo se decide lo que se ENSEÑA.
 *
 * Para volver a enseñar algo: poner su valor en `false`. No hay que tocar nada más.
 *
 * No confundir con `pantallasDeLaApp.js`: aquello son los interruptores de las pantallas
 * del CLIENTE, se guardan en la base y se cambian desde Ajustes sin desplegar.
 */
import { leerFiltros } from './filtrosEnLaDireccion';

export const OCULTO_EN_EL_PANEL = {
    // C5: la pestaña "Entreno" de la ficha de un cliente. Oculta por decisión de Francisco
    // (14-10), sabiendo que dentro viven el cardio pautado, el interruptor de la rutina
    // comprada aparte, equipamiento y lesiones, y el PDF de rutina de un cliente: mientras
    // esté oculta, eso no se toca desde el panel.
    // Vuelve a verse desde el 04-11 (Francisco: «no habilitaste rutinas en dev»): las pruebas
    // de la rutina del mes y la personalizada suben el PDF de un cliente desde aquí.
    pestanaEntrenoDeLaFicha: false,
    // C6: la entrada "Rutinas" del menú lateral (admin y entrenador). Vuelve el 04-11, con la
    // rutina del mes según el día de pago (Rutinas, «La rutina del mes»).
    rutinasEnElMenu: false,
    // C7: la entrada "Simulador de semanas" del menú lateral y los enlaces que llevan a él.
    simuladorDeSemanas: true,
    // Tarea 10 de la hoja del 15-10: la tarjeta «Piden que les llamemos» del Dashboard (se
    // lleva por fuera). Las peticiones siguen llegando y se ven en Leads; lo que se deja de
    // ver aquí son sus botones (llamar, enlace de pago, «Ponerle el plan pagado»).
    llamadasEnElPanel: true,
};

// LAS ENTRADAS QUE SE VEN PERO NO SE ABREN (apagadas en gris, sin enlace). El Dashboard
// desde el rediseño del 21-10; Tareas, Paneles, Accesos y Leads desde el 26-10 (lo pidió el
// equipo por WhatsApp el 24-10: «Deshabilitar del dashboard Admin»). Sus rutas siguen
// existiendo y se entra por dirección directa; volver a abrir una es quitarla de aquí.
// Con Leads apagado tampoco avisan los leads nuevos (insignia, campana y aviso flotante):
// llevarían a una entrada que no se abre. El aviso y el correo de «lead que ha pagado» SÍ
// siguen llevando a /admin/leads (es dinero y la ruta sigue abierta por dirección).
export const APAGADO_EN_EL_MENU = ['/admin', '/admin/tareas', '/admin/paneles', '/admin/accesos', '/admin/leads'];

// A qué interruptor responde cada entrada del menú lateral, por su ruta.
const ENTRADA_DEL_MENU = {
    '/admin/routines': 'rutinasEnElMenu',
    '/admin/simulador': 'simuladorDeSemanas',
};
// Y cada pestaña de la ficha, por su id.
const PESTANA_DE_LA_FICHA = {
    entrenamiento: 'pestanaEntrenoDeLaFicha',
};

const visibles = (lista, campo, mapa, oculto) => (lista || [])
    .filter((x) => !oculto[mapa[x?.[campo]]]);

/** Las entradas del menú lateral que se enseñan, con `disabled` en las apagadas (los dos
 *  últimos argumentos son para los tests). */
export const entradasDelMenu = (entradas, oculto = OCULTO_EN_EL_PANEL, apagado = APAGADO_EN_EL_MENU) =>
    visibles(entradas, 'path', ENTRADA_DEL_MENU, oculto)
        .map((x) => (apagado.includes(x.path) ? { ...x, disabled: true } : x));

/** ¿Avisan los leads nuevos? Solo si la entrada Leads se puede abrir. */
export const seAvisanLosLeads = (apagado = APAGADO_EN_EL_MENU) => !apagado.includes('/admin/leads');

/** Las pestañas de la ficha de un cliente que se enseñan. */
export const pestanasDeLaFicha = (pestanas, oculto = OCULTO_EN_EL_PANEL) =>
    visibles(pestanas, 'id', PESTANA_DE_LA_FICHA, oculto);

/** La pestaña con la que se abre la ficha (16-10): la que pide la dirección
 *  (`?pestana=seguimiento`, el botón de los correos al entrenador) si es una de las que se
 *  enseñan; si no, Resumen, como siempre. */
export const pestanaDeEntrada = (search, pestanas) =>
    leerFiltros(search, { pestana: 'resumen' }, { pestana: (pestanas || []).map((p) => p.id) }).pestana;

/** ¿Se enseñan los enlaces al simulador de semanas (el de cada regla en Documentación)? */
export const seEnlazaElSimulador = (oculto = OCULTO_EN_EL_PANEL) => !oculto.simuladorDeSemanas;

/** ¿Se enseña en el Dashboard la tarjeta «Piden que les llamemos»? */
export const seEnsenanLasLlamadas = (oculto = OCULTO_EN_EL_PANEL) => !oculto.llamadasEnElPanel;
